import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider, LangSync } from "./lib/i18n";
import Layout from "./components/Layout";
import { Loader2 } from "lucide-react";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import NewReport from "./pages/NewReport";
import DailyReports from "./pages/DailyReports";
import Indicators from "./pages/Indicators";
import WeeklyReport from "./pages/WeeklyReport";
import DosReport from "./pages/DosReport";
import DataQuality from "./pages/DataQuality";
import Signals from "./pages/Signals";
import Recommendations from "./pages/Recommendations";
import HealthZones from "./pages/HealthZones";
import Provinces from "./pages/Provinces";
import Administration from "./pages/Administration";
import MapView from "./pages/MapView";
import DataImport from "./pages/DataImport";
import LabDashboard from "./pages/LabDashboard";
import LabNewReport from "./pages/LabNewReport";
import PillarDashboard from "./pages/PillarDashboard";
import PillarNewReport from "./pages/PillarNewReport";
import Profile from "./pages/Profile";
import DownloadApp from "./pages/DownloadApp";
import Terrain from "./pages/Terrain";
import KoboSync from "./pages/KoboSync";
import DataQualityReview from "./pages/DataQualityReview";
import Actions from "./pages/Actions";
import ImportWord from "./pages/ImportWord";
import Overview from "./pages/Overview";
import MultiPillar from "./pages/MultiPillar";
import AlertsHistory from "./pages/AlertsHistory";
import MonthlyReports from "./pages/MonthlyReports";
import MonthlyReportForm from "./pages/MonthlyReportForm";
import UnifiedEntry from "./pages/UnifiedEntry";
import GeneratedReports from "./pages/GeneratedReports";
import Library from "./pages/Library";
import GeneratedReportView from "./pages/GeneratedReportView";
import EntryRegister from "./pages/EntryRegister";
import ConsolidatedReports from "./pages/ConsolidatedReports";
import ConsolidatedView from "./pages/ConsolidatedView";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

const P = (C) => <Protected>{C}</Protected>;

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.landing || "/dashboard"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
      <LangSync />
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={P(<RootRedirect />)} />
          <Route path="/dashboard" element={P(<Dashboard />)} />
          <Route path="/new-report" element={P(<NewReport />)} />
          <Route path="/reports" element={P(<DailyReports />)} />
          <Route path="/indicators" element={P(<Indicators />)} />
          <Route path="/map" element={P(<MapView />)} />
          <Route path="/weekly" element={P(<WeeklyReport />)} />
          <Route path="/dos" element={P(<DosReport />)} />
          <Route path="/data-quality" element={P(<DataQuality />)} />
          <Route path="/signals" element={P(<Signals />)} />
          <Route path="/recommendations" element={P(<Recommendations />)} />
          <Route path="/health-zones" element={P(<HealthZones />)} />
          <Route path="/provinces" element={P(<Provinces />)} />
          <Route path="/lab" element={P(<LabDashboard />)} />
          <Route path="/lab/new" element={P(<LabNewReport />)} />
          <Route path="/pillar/:code" element={P(<PillarDashboard />)} />
          <Route path="/pillar/:code/new" element={P(<PillarNewReport />)} />
          <Route path="/profile" element={P(<Profile />)} />
          <Route path="/download" element={P(<DownloadApp />)} />
          <Route path="/terrain" element={P(<Terrain />)} />
          <Route path="/kobo" element={P(<KoboSync />)} />
          <Route path="/qualite" element={P(<DataQualityReview />)} />
          <Route path="/actions" element={P(<Actions />)} />
          <Route path="/import-word" element={P(<ImportWord />)} />
          <Route path="/overview" element={P(<Overview />)} />
          <Route path="/multi-pillar" element={P(<MultiPillar />)} />
          <Route path="/alerts-history" element={P(<AlertsHistory />)} />
          <Route path="/entry" element={P(<UnifiedEntry />)} />
          <Route path="/register" element={P(<EntryRegister />)} />
          <Route path="/generated" element={P(<GeneratedReports />)} />
          <Route path="/library" element={P(<Library />)} />
          <Route path="/generated/:id" element={P(<GeneratedReportView />)} />
          <Route path="/consolidated" element={P(<ConsolidatedReports />)} />
          <Route path="/consolidated/:id" element={P(<ConsolidatedView />)} />
          <Route path="/monthly" element={P(<MonthlyReports />)} />
          <Route path="/monthly/:id" element={P(<MonthlyReportForm />)} />
          <Route path="/data-import" element={P(<DataImport />)} />
          <Route path="/admin" element={P(<Administration />)} />
          <Route path="*" element={P(<RootRedirect />)} />
        </Routes>
      </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
